export const exportNcrDocument = async (artifact) => {

  const issueDate = new Date().toLocaleDateString();

  async function generateChecksum(content) {

    const encoder = new TextEncoder();

    const data = encoder.encode(content);

    const hashBuffer =
      await crypto.subtle.digest(
        "SHA-256",
        data
      );

    const hashArray =
      Array.from(new Uint8Array(hashBuffer));

    return hashArray
      .map(b => b.toString(16).padStart(2, "0"))
      .join("");

  }

  const generatedAt =
    new Date().toISOString();

  const artifactId =
    artifact.id || crypto.randomUUID();

  const checksumPayload = JSON.stringify({

    transcript:
      artifact.transcript,

    workflowType:
      artifact.workflowType,

    decisionSignals:
      artifact.decisionSignals,

    evidenceImages:
      artifact.evidenceImages?.map(
        img => img.name
      ),

    audioBase64:
      artifact.audioBase64,

    generatedAt,

  });

  const checksum =
    await generateChecksum(
      checksumPayload
    );

  const imageSection =
    artifact.evidenceImages?.length > 0
      ? artifact.evidenceImages
          .map(
            (img) => `
              <div style="margin-bottom:16px;">

                <img
                  src="${img.base64}"
                  alt="${img.name}"
                  style="
                    max-width:100%;
                    max-height:420px;
                    object-fit:contain;
                    border:1px solid #ccc;
                    border-radius:6px;
                  "
                />

                <div
                  style="
                    font-size:11px;
                    color:#666;
                    margin-top:4px;
                  "
                >
                  ${img.name}
                </div>

              </div>
            `
          )
          .join("")
      : "<p>No image evidence attached.</p>";

  const audioSection =
    artifact.audioBase64
      ? `
        <div class="section-box">

          <div
            style="
              font-size:13px;
              color:#555;
              margin-bottom:10px;
            "
          >
            Original operator audio preserved
            for audit review.
          </div>

          <audio
            controls
            style="
              width:100%;
            "
          >
            <source
              src="${artifact.audioBase64}"
            />
          </audio>

        </div>
      `
      : `
        <div class="section-box">
          No audio evidence attached.
        </div>
      `;

  const html = `
  <html>

    <head>

      <meta charset="UTF-8" />

      <title>
        NCR-${artifactId}
      </title>

      <style>

        body {
          font-family: Arial, sans-serif;
          padding: 40px;
          color: #111;
          line-height: 1.5;
        }

        h1 {
          margin-bottom: 6px;
          color: #b91c1c;
        }

        h2 {
          margin-top: 36px;
          margin-bottom: 12px;
          padding-bottom: 6px;
          border-bottom: 1px solid #ddd;
          color: #1f2937;
        }

        table {
          width: 100%;
          border-collapse: collapse;
          margin-top: 12px;
        }

        td, th {
          border: 1px solid #ccc;
          padding: 10px;
          vertical-align: top;
          font-size: 14px;
        }

        .label {
          width: 220px;
          background: #f3f4f6;
          font-weight: bold;
        }

        .badge {
          display: inline-block;
          padding: 6px 10px;
          border-radius: 999px;
          font-size: 12px;
          font-weight: bold;
        }

        .high {
          background: #fee2e2;
          color: #991b1b;
        }

        .medium {
          background: #fef3c7;
          color: #92400e;
        }

        .low {
          background: #dcfce7;
          color: #166534;
        }

        .section-box {
          border: 1px solid #ddd;
          border-radius: 8px;
          padding: 16px;
          margin-top: 12px;
          background: #fafafa;
        }

        .signature-box {
          height: 80px;
        }

        .footer {
          margin-top: 50px;
          font-size: 12px;
          color: #666;
        }

      </style>

    </head>

    <body>

      <h1>
        NON-CONFORMANCE REPORT (NCR)
      </h1>

      <div style="margin-bottom:24px;">
        Generated by Fluxonix Operational Memory Layer
      </div>

      <table>

        <tr>
          <td class="label">
            NCR ID
          </td>

          <td>
            NCR-${artifactId}
          </td>
        </tr>

        <tr>
          <td class="label">
            Date
          </td>

          <td>
            ${issueDate}
          </td>
        </tr>

        <tr>
          <td class="label">
            Captured By
          </td>

          <td>
            ${artifact.capturedBy || "Unknown"}
          </td>
        </tr>

        <tr>
          <td class="label">
            Workflow Type
          </td>

          <td>
            ${artifact.workflowType || "Operational Workflow"}
          </td>
        </tr>

        <tr>
          <td class="label">
            Evidence Type
          </td>

          <td>
            ${artifact.evidenceType || "Operational Evidence"}
          </td>
        </tr>

        <tr>
          <td class="label">
            Severity
          </td>

          <td>

            <span class="badge ${
              artifact.riskLevel === "High" ||
              artifact.riskLevel === "Containment Required"
                ? "high"
                : artifact.riskLevel === "Medium"
                ? "medium"
                : "low"
            }">

              ${artifact.riskLevel || "Unknown"}

            </span>

          </td>
        </tr>

      </table>

      <h2>
        Original Audio Capture
      </h2>

      ${audioSection}

      <h2>
        Description of Discrepancy
      </h2>

      <div class="section-box">

        ${
          artifact.html ||
          "Operational event captured from frontline workflow."
        }

      </div>

      <h2>
        Containment Actions
      </h2>

      <div class="section-box">

        <ul>
          <li>
            Material/process flagged for review.
          </li>

          <li>
            Operational evidence linked to NCR packet.
          </li>

          <li>
            Further disposition pending quality review.
          </li>
        </ul>

      </div>

      <h2>
        Risk Signals
      </h2>

      <div class="section-box">

        <ul>

          ${(artifact.decisionSignals || [])
            .map(
              (signal) => `
                <li>${signal}</li>
              `
            )
            .join("")}

        </ul>

      </div>

      <h2>
        Evidence Package
      </h2>

      <div class="section-box">

        ${imageSection}

      </div>

      <h2>
        Disposition
      </h2>

      <table>

        <tr>
          <td>Use As-Is</td>
          <td></td>
        </tr>

        <tr>
          <td>Rework</td>
          <td></td>
        </tr>

        <tr>
          <td>Scrap</td>
          <td></td>
        </tr>

        <tr>
          <td>Return to Supplier</td>
          <td></td>
        </tr>

      </table>

      <h2>
        Approvals
      </h2>

      <table>

        <tr>
          <th>Role</th>
          <th>Name</th>
          <th>Signature</th>
          <th>Date</th>
        </tr>

        <tr class="signature-box">
          <td>Quality Engineer</td>
          <td></td>
          <td></td>
          <td></td>
        </tr>

        <tr class="signature-box">
          <td>Operations Manager</td>
          <td></td>
          <td></td>
          <td></td>
        </tr>

        <tr class="signature-box">
          <td>Supplier Representative</td>
          <td></td>
          <td></td>
          <td></td>
        </tr>

      </table>

      <div
        style="
          margin-top: 40px;
          padding-top: 16px;
          border-top: 1px solid #ccc;
          font-size: 12px;
          color: #666;
          line-height: 1.6;
        "
      >

        <div>
          <strong>Generated By:</strong>
          Fluxonix
        </div>

        <div>
          <strong>Generated At:</strong>
          ${generatedAt}
        </div>

        <div>
          <strong>Artifact ID:</strong>
          ${artifactId}
        </div>

        <div>
          <strong>SHA256 Checksum:</strong>
          ${checksum.slice(0, 32)}...
        </div>

        <div style="margin-top:8px;">
          Original operational evidence preserved by Fluxonix.
        </div>

      </div>

      <div class="footer">

        Fluxonix generated this NCR draft
        from frontline operational evidence,
        voice capture,
        and attached contextual signals.

      </div>

    </body>

  </html>
  `;

  const blob = new Blob(
    [html],
    { type: "text/html" }
  );

  const url =
    URL.createObjectURL(blob);

  const a =
    document.createElement("a");

  a.href = url;

  a.download =
    `NCR-${artifactId}.html`;

  document.body.appendChild(a);

  a.click();

  document.body.removeChild(a);

  URL.revokeObjectURL(url);

};